import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
// Custom layout to allow left nav card and right content card
import { FaSearch, FaUserFriends, FaUsers, FaMapMarkerAlt } from "react-icons/fa";
import { IoMdPeople } from "react-icons/io";
import * as userAPI from "../redux/api/userAPI";
import * as communityAPI from "../redux/api/communityAPI";

const Search = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const query = (params.get("q") || "").trim();
  const initialType = params.get("type") || "people";

  const [activeTab, setActiveTab] = useState(initialType === "communities" ? "communities" : "people");
  const [users, setUsers] = useState([]);
  const [communities, setCommunities] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      try {
        if (query) {
          const [{ data: usersData }, { data: communitiesData }] = await Promise.all([
            userAPI.searchUsers(query, 1),
            communityAPI.getAllCommunities(),
          ]);
          setUsers(usersData?.users || usersData || []);
          const filteredCommunities = (communitiesData || []).filter(c => (c?.name || "").toLowerCase().includes(query.toLowerCase()));
          setCommunities(filteredCommunities);
        } else {
          setUsers([]);
          setCommunities([]);
        }
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [query]);

  const header = useMemo(() => (
    <div className="flex items-center justify-between mb-4 border-b py-3 pl-2 pr-2">
      <div className="flex items-center gap-3">
        <FaSearch className="text-2xl text-primary-600" />
        <h2 className="text-lg font-semibold text-gray-700">Search Results</h2>
      </div>
      {query && <div className="text-sm font-medium text-gray-700">Results for "{query}"</div>}
    </div>
  ), [query]);

  // Tabs are implemented in the left sidebar card in this layout

  return (
    <div className="flex flex-col lg:flex-row gap-2 md:gap-3 max-w-7xl mx-auto px-2 md:px-0">
      {/* Left nav card */}
      <div className="hidden md:block lg:w-60 space-y-2 md:space-y-3 order-2 lg:order-1">
        <div className="bg-white rounded-lg p-3 md:p-4 shadow-sm">
          <div className="flex flex-col space-y-0">
            <button
              onClick={() => { setActiveTab("people"); navigate(`/skills-feed/search?q=${encodeURIComponent(query)}&type=people`); }}
              className={`flex gap-2 md:gap-3 items-center text-sm font-medium py-2 md:py-2.5 px-2 md:px-3 rounded-lg transition-all duration-200 ${activeTab === 'people' ? 'text-primary-700 bg-primary-50' : 'text-gray-700 hover:text-primary-800 hover:bg-gray-50'}`}
            >
              <FaUserFriends className={`text-base md:text-lg ${activeTab === 'people' ? 'text-primary-700' : 'text-gray-500'}`} />
              <span className="font-roboto-flex text-sm md:text-base">People</span>
            </button>
            <button
              onClick={() => { setActiveTab("communities"); navigate(`/skills-feed/search?q=${encodeURIComponent(query)}&type=communities`); }}
              className={`flex gap-2 md:gap-3 items-center text-sm font-medium py-2 md:py-2.5 px-2 md:px-3 rounded-lg transition-all duration-200 ${activeTab === 'communities' ? 'text-primary-700 bg-primary-50' : 'text-gray-700 hover:text-primary-800 hover:bg-gray-50'}`}
            >
              <FaUsers className={`text-base md:text-lg ${activeTab === 'communities' ? 'text-primary-700' : 'text-gray-500'}`} />
              <span className="font-roboto-flex text-sm md:text-base">Communities</span>
            </button>
          </div>
        </div>
      </div>

      {/* Center content */}
      <div className="flex-1 w-full lg:max-w-6xl order-1 lg:order-2 min-w-0">
        <div className="bg-white border rounded-lg">
          <div className="p-3">
            {header}
            {loading ? (
              <div className="py-12 text-center text-gray-500">Searching...</div>
            ) : (
              <>
                {activeTab === "people" && (
                  <div className="space-y-2">
                    {users.length === 0 ? (
                      <div className="text-center text-gray-500 py-8">No users found.</div>
                    ) : (
                      users.map(user => (
                        <div key={user._id} className="flex items-center gap-3 p-4 rounded-lg border hover:bg-gray-50 cursor-pointer transition-colors" onClick={() => navigate(`/profile/${user.profileUrl || user._id}`)}>
                          <img src={user.avatar} alt={user.name} className="w-12 h-12 rounded-full object-cover flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-gray-800 truncate mb-1">{user.name}</div>
                            <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                              {(user.city || user.country) && (
                                <div className="flex items-center gap-1">
                                  <FaMapMarkerAlt className="w-3 h-3" />
                                  <span>{[user.city, user.country].filter(Boolean).join(", ")}</span>
                                </div>
                              )}
                              {user.followerCount !== undefined && (
                                <div className="flex items-center gap-1">
                                  <FaUserFriends className="w-3 h-3" />
                                  <span>{user.followerCount} follower{user.followerCount !== 1 ? 's' : ''}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}

                {activeTab === "communities" && (
                  <div className="space-y-2">
                    {communities.length === 0 ? (
                      <div className="text-center text-gray-500 py-8">No communities found.</div>
                    ) : (
                      communities.map(c => (
                        <div key={c._id || c.name} className="flex items-center gap-3 p-4 rounded-lg border hover:bg-gray-50 cursor-pointer transition-colors" onClick={() => navigate(`/skills-feed/${c.name}`)}>
                          <img src={c.banner} alt={c.name} className="w-12 h-12 rounded-full object-cover flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-gray-800 truncate mb-1">{c.name}</div>
                            <div className="flex items-center gap-3 text-xs text-gray-500">
                              {c.members && (
                                <div className="flex items-center gap-1">
                                  <IoMdPeople className="w-3 h-3" />
                                  <span>{c.members.length} member{c.members.length !== 1 ? 's' : ''}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Right content section card */}
      <div className="hidden md:block lg:w-60 space-y-2 md:space-y-3 order-3">
        <div className="bg-white rounded-lg border p-4 shadow-sm">
          <h5 className="mb-4 text-sm font-semibold font-roboto-flex">Content Section</h5>
          <div className="text-center text-gray-500 font-roboto-flex py-8">
            <div className="text-4xl mb-3">🚀</div>
            <p className="text-sm">Coming Soon</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Search;


